import React, { useCallback, useEffect, useState, useContext, useMemo } from "react";
import { Box } from "@mui/material";
import { useEnglishText } from "../../../text/useEnglishText.js";
import DocumentsList from "../../../components/layout/DocumentsList";
import CompanyManageModal from "./CompanyManageModal";
import { getPartitionCompanies } from "../../../services/companyService";
import { AuthContext } from "../../../context/AuthContext";
import AdminPageHeader from "../../../components/common/AdminPageHeader";
import EmptyState from "../../../components/common/EmptyState";
import { Building2 } from "lucide-react";
import { Button } from "../../../components/ui/button";

const isEmptyString = (value) =>
  value === null || value === undefined || String(value).trim() === "";
function Companies() {
  const { t } = useEnglishText();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const [openModal, setOpenModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { user } = useContext(AuthContext);

  const hasNoActiveLicense = useMemo(() => {
    const setupCompleted = user?.SETUP_COMPLETED;
    const trialEligible = user?.TRIAL_ELIGIBLE;
    const trialStartDate = user?.TRIAL_START_DATE;
    const trialEndDate = user?.TRIAL_END_DATE;
    const url = user?.URL;

    return (
      isEmptyString(setupCompleted) &&
      isEmptyString(trialEligible) &&
      isEmptyString(trialStartDate) &&
      isEmptyString(trialEndDate) &&
      isEmptyString(url)
    );
  }, [user]);
  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getPartitionCompanies();
      const list = Array.isArray(res) ? res : res?.data || res?.Data || [];
      setCompanies(list);
    } catch (error) {
      console.error("Failed to load companies:", error);
      setError(t("companies.fetch_error", "Failed to load companies."));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSelect = (row) => {
    // ردیف انتخاب شده شامل فیلد raw (کل دیتای کمپانی) است
    setSelectedCompany(row?.raw || row);
    setOpenModal(true);
  };

  const handleAddCompany = () => {
    setSelectedCompany(null);
    setOpenModal(true);
  };
  if (hasNoActiveLicense) {
    return (
      <Box>
        <AdminPageHeader icon={Building2} title={t("companies.title", "Companies")} description={t("companies.description")} />
        <Box sx={{ maxWidth: 700, mx: "auto" }}>
          <EmptyState
            title={t("licenses.no_active_license_title", "No active license found")}
            description={t("licenses.no_active_license_message", "Submit a license request to continue.")}
            action={<Button variant="outline" asChild><a href="/dashboard/license-requests">{t("licenses.no_active_license_button", "Go to License Requests")}</a></Button>}
          />
        </Box>
      </Box>
    );
  }
  return (
    <Box>
      <AdminPageHeader
        icon={Building2}
        title={t("companies.title", "Companies")}
        description={t("companies.description")}
      />

      <DocumentsList
        data={companies}
        loading={loading}
        error={error}
        emptyTitle={t("companies.empty", "No companies found")}
        onSelect={handleSelect}
        page={page}
        pageSize={pageSize}
        paginateLocally
        onPageChange={(p) => setPage(p)}
        actions={<Button variant="outline" onClick={handleAddCompany}>{t("companies.add_company", "Add Company")}</Button>}
      />

      <CompanyManageModal
        open={openModal}
        company={selectedCompany}
        onClose={() => {
          setOpenModal(false);
          setSelectedCompany(null);
        }}
        onSuccess={fetchCompanies}
      />
    </Box>
  );
}

export default Companies;
